import { type FunctionComponent, lazy, type ReactNode, Suspense } from 'react';
import {
  createBrowserRouter,
  Navigate,
  type RouteObject,
  RouterProvider,
} from 'react-router';

import { Loader } from '../components';
import { useAuth } from '../services/auth/auth.context.tsx';

const CreatePatientPage = lazy(() =>
  import('./CreatePatient/CreatePatient.page.tsx').then((m) => ({
    default: m.CreatePatientPage,
  })),
);
const CreateScorePage = lazy(() =>
  import('./CreateScore/CreateScore.page.tsx').then((m) => ({
    default: m.CreateScorePage,
  })),
);
const LabelPage = lazy(() =>
  import('./Label/Label.page.tsx').then((m) => ({ default: m.LabelPage })),
);
const LoginPage = lazy(() =>
  import('./Login/Login.page.tsx').then((m) => ({ default: m.LoginPage })),
);
const NotFoundPage = lazy(() =>
  import('./NotFound/NotFound.page.tsx').then((m) => ({
    default: m.NotFoundPage,
  })),
);
const PatientDashboardPage = lazy(() =>
  import('./PatientDashboard/PatientDashboard.page.tsx').then((m) => ({
    default: m.PatientDashboardPage,
  })),
);
const PatientListPage = lazy(() =>
  import('./PatientList/PatientList.page.tsx').then((m) => ({
    default: m.PatientListPage,
  })),
);
const PublicFormPage = lazy(() =>
  import('./PublicForm/PublicForm.page.tsx').then((m) => ({
    default: m.PublicFormPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import('./ResetPassword/ResetPassword.page.tsx').then((m) => ({
    default: m.ResetPasswordPage,
  })),
);

const RouteFallback: FunctionComponent = () => (
  <Loader color="dark" size="medium" />
);

const ProtectedRoute: FunctionComponent<{ children: ReactNode }> = ({
  children,
}) => {
  const { user, initialized } = useAuth();

  if (!initialized) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
};

const PublicRoute: FunctionComponent<{ children: ReactNode }> = ({
  children,
}) => {
  const { user, initialized } = useAuth();

  if (!initialized) {
    return <div>Loading...</div>;
  }

  if (user) {
    return <Navigate to="/patient-list" replace />;
  }

  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
};

const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <PublicRoute>
        <LoginPage />
      </PublicRoute>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <PublicRoute>
        <ResetPasswordPage />
      </PublicRoute>
    ),
  },
  {
    path: '/public',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <PublicFormPage />
      </Suspense>
    ),
  },
  {
    path: '/patient-list',
    element: (
      <ProtectedRoute>
        <PatientListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/create-patient',
    element: (
      <ProtectedRoute>
        <CreatePatientPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/patient/:patientId/create-score',
    element: (
      <ProtectedRoute>
        <CreateScorePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/patient/:patientId/dashboard',
    element: (
      <ProtectedRoute>
        <PatientDashboardPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/label',
    element: (
      <ProtectedRoute>
        <LabelPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/not-found',
    element: (
      <ProtectedRoute>
        <NotFoundPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/*',
    element: <Navigate to="/not-found" replace />,
  },
];

const router = createBrowserRouter(routes);

export const Router: FunctionComponent = () => {
  return <RouterProvider router={router} />;
};
